<template>
  <div class="subscriptions-list">
    <div
      v-if="isTeamsSelected && showB2BBanner"
      class="subscriptions-list__products-plans-teams-banner"
    >
      <a target="_blank" :href="teamsBannerLink">
        <img
          :src="teamsBannerImg"
          class="subscriptions-list__products-plans-teams-banner-img"
          alt="Buy Enterprise licenses and get free certification vouchers with every seat.*"
        />
      </a>
    </div>
    <h1
      v-if="!isOnePlanAvailable"
      :class="[
        'subscriptions-list__title',
        'subscriptions-list__title--bold',
        isTeamsSelected && 'subscriptions-list__title--margin-top',
      ]"
    >
      Subscriptions
    </h1>
    <div v-if="!isOnePlanAvailable" class="subscriptions-list__intro">
      Whether for individuals or teams, INE has an IT training solution. Choose from our
      subscription plans and start your upskilling journey.
    </div>
    <div class="subscriptions-list__subscribed" v-if="userHasSubscription && !isBusinessUser">
      <div class="subscriptions-list__subscribed-title">You have an INE subscription</div>
      <div class="subscriptions-list__subscribed-subtitle">Visit my.ine.com to change plans</div>
      <div class="subscriptions-list__subscribed-cta">
        <a :href="streamingLink" class="btn btn--primary"> Change plans </a>
      </div>
    </div>
    <div class="subscriptions-list__subscribed" v-if="isBusinessUser">
      <div class="subscriptions-list__subscribed-title">
        {{ isBusinessPlanExpired ? 'Your Business plan has expired' : 'You have a Business plan' }}
      </div>
      <div v-if="isBusinessPlanExpired" class="subscriptions-list__subscribed-subtitle">
        To receive Business plan options please contact your business account owner
      </div>
    </div>
    <div
      class="subscriptions-list__plans"
      v-if="!userHasSubscription && !isBusinessUser && isOnePlanAvailable"
    >
      <PlanListItemRow :plans="individualPlans" />
    </div>
    <div
      class="subscriptions-list__plans"
      v-if="!userHasSubscription && !isBusinessUser && !isOnePlanAvailable"
    >
      <transition
        name="subscriptions-list__fade"
        mode="out-in"
        class="subscriptions-list__products"
      >
        <div
          v-if="!isTeamsSelected"
          class="subscriptions-list__products-plans-individuals"
          key="individuals-plans"
        >
          <div class="subscriptions-list__products-plans-individuals-row">
            <PlanRowItem
              v-for="plan in filteredPlans"
              :key="plan.id"
              :plan="plan"
              :currentPlans="individualPlans"
              class="subscriptions-list__products-plans-individuals-item"
              @toggle-card="toggleCard"
              :small="filteredPlans.length > 2"
            />
          </div>
        </div>
        <div
          v-if="isTeamsSelected"
          class="subscriptions-list__products-plans-teams"
          key="teams-plans"
        >
          <PlanListTeamsDetails />
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import CONSTANTS from '@/constants'
import { updateCart } from '@/helpers/mixins'
import PlanListItemRow from '@/components/plan-item-card/PlanListItemRow.vue'
import PlanRowItem from '@/components/plan-item-card/PlanRowItem.vue'
import PlanListTeamsDetails from '@/components/plan-item-card/PlanListTeamsDetails.vue'
import TeamsTabPng from '@/assets/images/all-promos/teams-tab-banner.png'

const { PLANS, TCS_PROMO_PRODUCT_BUNDLES, LINKS } = CONSTANTS
const { SPRING_SAVINGS_2025 } = LINKS

export default {
  name: 'SubscriptionsList',
  mixins: [updateCart],
  components: {
    PlanListTeamsDetails,
    PlanListItemRow,
    PlanRowItem,
  },
  data() {
    return {
      filteredIndividualPlans: [],
      individualPlans: [],
      teamsBannerLink: SPRING_SAVINGS_2025,
      teamsBannerImg: TeamsTabPng,
    }
  },
  computed: {
    ...mapState({
      usersCurrentSubscriptions: state => state.subscriptions.usersCurrentSubscriptions,
      isOnePlanAvailable: state => state.products.isOnePlanAvailable,
      isPremiumMonthlyPromoActive: state => state.app.isPremiumMonthlyPromoActive,
      isEDAPremiumPreSaleActive: state => state.app.isEDAPremiumPreSaleActive,
      availableProductBundles: state => state.products.availableProductBundles,
    }),
    ...mapGetters({
      plans: 'products/plansByTerm',
      availableProductBundles: 'products/availableProductBundles',
      isBusinessUser: 'business/isBusinessUser',
      isBusinessPlanExpired: 'business/isBusinessPlanExpired',
      isPromotionActive: 'app/isPromotionActive',
      isTeamsSelected: 'app/isB2BOfferView',
      showB2BBanner: 'featureFlags/showB2BBanner',
      premiumPlusEnabled: 'featureFlags/premiumPlusEnabled',
      isPremiumPlusEnabledForUser: 'subscriptions/isPremiumPlusEnabledForUser',
    }),
    userHasSubscription() {
      return this.usersCurrentSubscriptions.length > 0
    },
    streamingLink() {
      return `//${process.env.VUE_APP_STREAMING_URL}/dashboard/profile`
    },
    filteredPlans() {
      return this.individualPlans.filter(plan => this.displayItemPlan(plan))
    },
  },
  created() {
    this.setIndividualPlans()
  },
  methods: {
    displayItemPlan(plan) {
      if (plan.selected === undefined) return true
      return !!plan.selected
    },
    toggleCard(plans, selectedPlan) {
      const merchantIds = plans.map(p => p.merchant_id)
      this.individualPlans = this.individualPlans.map(plan => {
        if (!merchantIds.includes(plan.merchant_id)) return plan
        const selected = selectedPlan === plan.merchant_id
        return { ...plan, selected }
      })
    },
    setIndividualPlans() {
      if (this.isOnePlanAvailable) {
        this.individualPlans = this.plans.yearly.filter(p => [PLANS.BAH].includes(p.merchant_id))
        return
      }

      let fundamentalsAnnualAux = {}
      let bestValuePlanAux = {}
      let premiumPlan = {}
      this.plans.yearly.forEach(p => {
        if (p.merchant_id === PLANS.PREMIUM) {
          p.isBestValue = true
          bestValuePlanAux = p
          premiumPlan = p
        }
        if (p.merchant_id === PLANS.ANNUAL) {
          p.selected = true
          fundamentalsAnnualAux = p
        }
      })
      if (this.isTCSPromoEnabled) {
        bestValuePlanAux = this.availableProductBundles.find(
          pb => pb.slug === TCS_PROMO_PRODUCT_BUNDLES.INE_TCS_BUNDLE
        )
      }
      // currently we have only one monthly plan: fundamentalsMonthly
      const fundamentalsMonthly = this.plans.monthly.find(p => {
        p.selected = false
        return PLANS.MONTHLY === p.merchant_id
      })

      // the order of the plans is important for the UI to work properly
      this.individualPlans = [fundamentalsMonthly, fundamentalsAnnualAux, bestValuePlanAux]

      if (this.isPremiumPlusEnabledForUser) {
        bestValuePlanAux = this.plans?.yearly.find(p => p.merchant_id === PLANS.PREMIUM_PLUS)
        this.individualPlans = [premiumPlan, fundamentalsMonthly, fundamentalsAnnualAux]

        if (bestValuePlanAux) {
          premiumPlan.isBestValue = false
          bestValuePlanAux.isBestValue = true
          this.individualPlans.unshift(bestValuePlanAux)
        }
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.subscriptions-list {
  &__title {
    font-weight: 450;
    font-size: 24px;
    line-height: 32px;
    margin: 0;
    margin-bottom: 10px;
    &--bold {
      font-size: rem(24px);
      line-height: rem(32px);
      font-style: normal;
      font-weight: 700;
      margin-bottom: 10px;
    }
    &--margin-top {
      margin-top: 10px;
    }
  }
  &__intro {
    color: var(--light-blue-gray);
    font-size: rem(18px);
    font-style: normal;
    font-weight: 450;
    line-height: rem(24px);
    margin-bottom: 50px;
  }
  &__subscriptions {
    border-bottom: 1px solid var(--mid-gray-translucid);
    margin-bottom: 60px;
  }
  &__subscribed {
    padding: 40px;
    background: var(--mid-gray-gradient);
    border-radius: 10px;
    color: var(--white);
    font-family: var(--font-regular);
    display: flex;
    justify-content: center;
    flex-flow: column;
    align-items: center;
    margin-bottom: 60px;
    &-title {
      font-weight: 700;
      font-size: 32px;
      line-height: 38px;
      margin-bottom: 10px;
      text-align: center;
    }
    &-subtitle {
      font-weight: 450;
      font-size: 16px;
      line-height: 24px;
      margin-bottom: 30px;
      color: var(--dusty-gray);
    }
    &-cta {
      .btn {
        text-decoration: none;
      }
    }
  }
  &__products-plans {
    &-individuals {
      &-row {
        display: flex;
        flex-flow: column;
        gap: 20px;
        align-items: flex-start;
        @include tablet {
          flex-flow: row;
        }
      }
    }
    &-teams {
      &-banner {
        margin-bottom: 30px;
        &-img {
          width: 100%;
          border-radius: 10px;
        }
      }
    }
  }
  &__fade-enter-active,
  &__fade-leave-active {
    transition: all 0.3s ease-in-out;
    opacity: 1;
  }
  &__fade-enter,
  &__fade-leave-to {
    transition: all 0.3s ease-in-out;
    opacity: 0;
  }
}
</style>
